import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiConversion,
  ApiConversionType,
  ApiLocation,
  ApiReservedConversion,
} from "@incendium/api";
import { Button, IconButton, Stack, Switch, TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import StyledDrawer, {
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import { ReservedConversionSelector } from "./ReservedConversionSelector";
import { useNotification } from "Hooks";
import { attachConversion, detachConversion, saveConversion } from "features/location";
import { Launch, LinkOff } from "@mui/icons-material";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";

type ConversionSettingsDrawerProps = {
  open: boolean;
  conversion: ApiConversion | null;
  locations: ApiLocation[];
  clientId?: number;
  onClose: () => void;
  onConversionUpdated?: () => Promise<void> | void;
  onOpenLocation?: (conversion: ApiConversion, location: ApiLocation) => void;
};

function ConversionSettingsDrawer({
  open,
  conversion,
  locations,
  clientId,
  onClose,
  onConversionUpdated,
  onOpenLocation,
}: ConversionSettingsDrawerProps) {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [name, setName] = useState<string>(conversion?.name || "");
  const [type, setType] = useState<ApiConversionType>(
    conversion?.type || ApiConversionType.MICRO
  );
  const [reserved, setReserved] = useState<ApiReservedConversion | undefined>(
    conversion?.reserved
  );
  const [savingDetails, setSavingDetails] = useState(false);
  const [selectedAttachLocationId, setSelectedAttachLocationId] = useState<number | null>(null);
  const [attachLoading, setAttachLoading] = useState(false);
  const [detachingLocationIds, setDetachingLocationIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    setName(conversion?.name || "");
    setType(conversion?.type || ApiConversionType.MICRO);
    setReserved(conversion?.reserved);
    setSelectedAttachLocationId(null);
    setDetachingLocationIds(new Set());
  }, [conversion, open]);

  const attachedLocationIds = useMemo(() => {
    if (!conversion) {
      return new Set<number>();
    }
    return new Set(
      (conversion.locations || [])
        .map((loc) => loc?.id)
        .filter((id): id is number => id != null)
    );
  }, [conversion]);

  const attachableLocations = useMemo(() => {
    return locations.filter(
      (location): location is ApiLocation =>
        location.id != null && !attachedLocationIds.has(location.id)
    );
  }, [attachedLocationIds, locations]);

  useEffect(() => {
    if (open && attachableLocations.length > 0) {
      setSelectedAttachLocationId((current) => {
        if (current && attachableLocations.some((loc) => loc.id === current)) {
          return current;
        }
        return attachableLocations[0]?.id ?? null;
      });
    } else if (!open) {
      setSelectedAttachLocationId(null);
    }
  }, [open, attachableLocations]);

  const handleClose = useCallback(() => {
    if (savingDetails || attachLoading || detachingLocationIds.size > 0) {
      return;
    }
    onClose();
  }, [attachLoading, detachingLocationIds.size, onClose, savingDetails]);

  const hasDetailsChanged = useMemo(() => {
    return !!conversion &&
      (name !== (conversion.name || "") ||
        type !== (conversion.type || ApiConversionType.MICRO) ||
        (reserved || null) !== (conversion.reserved || null));
  }, [conversion, name, type, reserved]);

  const firstAttachedLocation = useMemo(() => {
    if (!conversion) {
      return null;
    }
    return (
      conversion.locations?.find((loc) => loc?.id != null) || null
    );
  }, [conversion]);

  const projectIdFromConversion =
    (conversion?.projectId as number | undefined) ||
    (firstAttachedLocation?.projectId as number | undefined) ||
    undefined;

  const handleSaveDetails = useCallback(async () => {
    if (!conversion) {
      showErrorNotification("No conversion selected");
      return;
    }
    if (!clientId) {
      showErrorNotification("Client information missing");
      return;
    }
    if (!firstAttachedLocation || firstAttachedLocation.id == null) {
      showErrorNotification("Conversion is not attached to any location");
      return;
    }
    const projectId =
      projectIdFromConversion ||
      (conversion.projectId as number | undefined) ||
      (firstAttachedLocation.projectId as number | undefined);
    if (!projectId) {
      showErrorNotification("Project information missing");
      return;
    }

    try {
      setSavingDetails(true);
      await saveConversion(clientId, projectId, firstAttachedLocation.id, {
        ...conversion,
        name,
        type,
        reserved,
      });
      showSuccessNotification("Conversion updated");
      await onConversionUpdated?.();
    } catch (error) {
      showErrorNotification("Failed to update conversion");
    } finally {
      setSavingDetails(false);
    }
  }, [
    clientId,
    conversion,
    firstAttachedLocation,
    name,
    onConversionUpdated,
    projectIdFromConversion,
    reserved,
    showErrorNotification,
    showSuccessNotification,
    type,
  ]);

  const handleDetachLocation = useCallback(
    async (location: ApiLocation) => {
      if (!conversion || !clientId || location.id == null || conversion.id == null) {
        showErrorNotification("Missing information to detach");
        return;
      }
      const projectId =
        (location.projectId as number | undefined) ||
        projectIdFromConversion ||
        (conversion.projectId as number | undefined);
      if (!projectId) {
        showErrorNotification("Project information missing for the selected location");
        return;
      }
      setDetachingLocationIds((prev) => new Set(prev).add(location.id!));
      try {
        await detachConversion(
          clientId,
          projectId,
          location.id,
          conversion.id
        );
        showSuccessNotification("Conversion detached from location");
        await onConversionUpdated?.();
      } catch (error) {
        showErrorNotification("Failed to detach conversion");
      } finally {
        setDetachingLocationIds((prev) => {
          const next = new Set(prev);
          next.delete(location.id!);
          return next;
        });
      }
    },
    [
      clientId,
      conversion,
      onConversionUpdated,
      projectIdFromConversion,
      showErrorNotification,
      showSuccessNotification,
    ]
  );

  const handleAttachLocation = useCallback(async () => {
    if (!conversion || !clientId) {
      showErrorNotification("Missing conversion information");
      return;
    }
    if (selectedAttachLocationId == null) {
      showErrorNotification("Please select a location to attach");
      return;
    }
    const targetLocation = attachableLocations.find(
      (loc) => loc.id === selectedAttachLocationId
    );
    if (!targetLocation || targetLocation.id == null) {
      showErrorNotification("Location not found");
      return;
    }
    const projectId =
      (targetLocation.projectId as number | undefined) ||
      projectIdFromConversion ||
      (conversion.projectId as number | undefined);
    if (!projectId) {
      showErrorNotification("Project information missing");
      return;
    }
    try {
      setAttachLoading(true);
      await attachConversion(clientId, projectId, targetLocation.id, conversion);
      showSuccessNotification("Conversion attached to location");
      await onConversionUpdated?.();
      setSelectedAttachLocationId(null);
    } catch (error) {
      showErrorNotification("Failed to attach conversion");
    } finally {
      setAttachLoading(false);
    }
  }, [
    attachableLocations,
    clientId,
    conversion,
    onConversionUpdated,
    projectIdFromConversion,
    selectedAttachLocationId,
    showErrorNotification,
    showSuccessNotification,
  ]);

  return (
    <StyledDrawer open={open} onClose={handleClose}>
      <StyledDrawerTitle>
        <Typography variant="h6">
          {conversion?.name ? `Conversion settings — ${conversion.name}` : "Conversion settings"}
        </Typography>
      </StyledDrawerTitle>


      {conversion ? (
        <>
          <AccordianChartBuilderSidebarBlock
            title="Conversion details"
            subTitle="Edit the core settings for this conversion"
            defaultClosed={false}
          >
            <Stack spacing={2}>
              <TextField
                label="Conversion name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                fullWidth
                disabled={savingDetails}
              />
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography>Interaction</Typography>
                <Switch
                  checked={type === ApiConversionType.MACRO}
                  onChange={(_, checked) =>
                    setType(checked ? ApiConversionType.MACRO : ApiConversionType.MICRO)
                  }
                  disabled={savingDetails}
                />
                <Typography>Conversion</Typography>
              </Stack>
              <ReservedConversionSelector
                value={reserved}
                onChange={(event) =>
                  setReserved(event.target.value as ApiReservedConversion | undefined)
                }
                disabled={savingDetails}
              />
              {!firstAttachedLocation ? (
                <Typography variant="body2" color="text.secondary">
                  Attach this conversion to at least one location to update its settings.
                </Typography>
              ) : null}
              <Stack direction="row" justifyContent={"flex-end"}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSaveDetails}
                  disabled={
                    !conversion || !hasDetailsChanged || savingDetails || !firstAttachedLocation
                  }
                >
                  {savingDetails ? "Saving..." : "Save changes"}
                </Button>
              </Stack>
            </Stack>
          </AccordianChartBuilderSidebarBlock>

          <AccordianChartBuilderSidebarBlock
            title="Manage locations"
            subTitle="See where this conversion is used and add or remove locations"
            defaultClosed={false}
          >
            <Stack spacing={3}>
              <Stack spacing={1}>
                {(conversion.locations || []).length === 0 ? (
                  <Typography variant="body2">Not attached to any locations yet.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {(conversion.locations || []).map((location) => {
                      if (!location || location.id == null) {
                        return null;
                      }
                      const isDetaching = detachingLocationIds.has(location.id);
                      return (
                        <Stack
                          key={location.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                          }}
                        >
                          <Stack spacing={0.5}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Typography
                                variant="body1"
                                color="text.primary"
                                sx={{
                                  cursor: onOpenLocation ? "pointer" : "default",
                                  textDecoration: "none",
                                  "&:hover": onOpenLocation ? { textDecoration: "underline" } : {},
                                }}
                                onClick={() =>
                                  onOpenLocation &&
                                  conversion &&
                                  onOpenLocation(conversion, location)
                                }
                              >
                                {location.name || "Unnamed location"}
                              </Typography>
                              {onOpenLocation ? (
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    onOpenLocation &&
                                    conversion &&
                                    onOpenLocation(conversion, location)
                                  }
                                >
                                  <Launch fontSize="inherit" />
                                </IconButton>
                              ) : null}
                            </Stack>
                            {location.urlMatch ? (
                              <Typography variant="caption" color="text.secondary">
                                {location.urlMatch}
                              </Typography>
                            ) : null}
                          </Stack>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleDetachLocation(location)}
                            disabled={isDetaching || savingDetails}
                          >
                            <LinkOff fontSize="small" />
                          </IconButton>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Stack>

              <Stack spacing={2}>
                <Typography variant="subtitle2">Attach to another location</Typography>
                {attachableLocations.length > 0 ? (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Autocomplete
                      sx={{ flex: 1 }}
                      size="small"
                      options={attachableLocations}
                      value={
                        attachableLocations.find((loc) => loc.id === selectedAttachLocationId) ||
                        null
                      }
                      onChange={(_, value) => setSelectedAttachLocationId(value?.id ?? null)}
                      getOptionLabel={(option) => option.name || ""}
                      renderInput={(params) => (
                        <TextField {...params} label="Select location" variant="outlined" />
                      )}
                      disabled={attachLoading || savingDetails}
                    />
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={handleAttachLocation}
                      disabled={
                        attachLoading ||
                        savingDetails ||
                        selectedAttachLocationId == null ||
                        attachableLocations.length === 0
                      }
                    >
                      {attachLoading ? "Attaching..." : "Attach"}
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    This conversion is already attached to every available location.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </AccordianChartBuilderSidebarBlock>
        </>
      ) : (
        <Typography variant="body2">Select a conversion to manage its settings.</Typography>
      )}
    </StyledDrawer>
  );
}

export default ConversionSettingsDrawer;

